import { logEvent } from '@mfs/client/common/log-ga-event/logGaEvent'
import { countFlightPassengers } from '@mfs/client/details-client/DetailsCTA/helper'
import PCLNAnalytics from '@pcln/analytics'
import pclnExpt from '@pcln/experimentation'
import { useFeature } from '@pcln/feature-components'
import {
  DepartureSummaryCarousel,
  ExpressDealCard,
  ListingsHeader,
  PriceDisclosure,
  RetailItinerary,
} from '@pcln/fly-ui-components'
import { ListingCard, DepartureSummary } from '@pcln/horizon-fly-components'
import { PackageListingHeaderPrice } from '@pcln/horizon-package-components'
import { AnchoredToastViewport, ToastViewport, Highlight } from '@pcln/horizon'
import classNames from 'classnames'
import get from 'lodash/get'
import { Badge, Box, Flex, Heading, SrOnly } from 'pcln-design-system'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import { logNoBrandedFareSecondSliceEvent } from '../../../common/log-ga-event/logGaEvent-bf-shelves'
import { logIconTray } from '../../../common/log-ga-event/logGaEvent-icon-tray.js'
import { logViewAllOptionsClick } from '../../../common/log-ga-event/logGaEvent-internal-link'
import { logInternalLink } from '../../../common/log-ga-event/logGaEvent-select-item-listings'
import { logExdCreateAccountClick } from '@mfs/client/common/log-ga-event/logGaEvent-exd-create-account-click'
import { logExdSigninClick } from '@mfs/client/common/log-ga-event/logGaEvent-exd-signin-click'
import { buildNumOfItinsReturnedText } from '../../util/buildScreenReaderText'
import { RecommendedFilters } from '../Filters/RecommendedFilters'
import {
  getIsAllProposalPricesMissing,
  getPackageListingHeaderProps,
  isNumOfAdultPassengers,
} from '../ListingsContent/helpers'
import { PackageListingHeaderRow } from './PackageListingHeaderRow'
import { PackagesVIPBanner } from '../PackagesVIPBanner'
import { RecommendedFlightBannerWrapper } from '../RecommendedFlightBannerWrapper'
import { WrapComponentsRenderInView } from '../WrapComponentsRenderInView'
import { getExdProps, hasCheckedBagOnAllSlices } from './helpers/expressDealHelpers'
import { useDepartureSummary } from './hooks/useDepartureSummary'
import { useExpressDealSignInToast } from './hooks/useExpressDealSignInToast'
import { usePkgFlyListingVipBadge } from './hooks/usePkgFlyListingVipBadge'
import { RecommendedListingsHeader } from './RecommendedListingsHeader'
import {
  getNextSliceClick,
  getPriceByType,
  getSBSItineraryData,
  goToCheckout,
  goToDetailsClick,
  returnToDepartureTab,
} from './SBSHelpers/SBSHelpers'
import { calculateBannerIndex } from './helpers/listingsUtils'
import { insertDesktopStaticBannerPositionNodes } from './helpers/pkgDesktopStaticBannerPlacement'
import { countFlightListingItems, getPhoneSalesBannerInsertIndex } from './helpers/phoneSalesBannerPlacement'

const ItineraryList = styled.ul`
  padding-left: 0 !important;
`

const StyledRetailItinerary = styled(RetailItinerary)`
  margin: 0 8px 0 8px;

  ${(props) => props.theme?.mediaQueries?.xl} {
    margin: 0;
  }
`

const DepartureSummaryWrapper = styled(Box)`
  ${(props) =>
    props.isSticky &&
    css`
      z-index: 98;
      position: sticky;
      top: ${props.stickyTop};
    `}

  ${({ theme }) => theme.mediaQueries.lg} {
    ${({ isSticky }) =>
      isSticky &&
      css`
        z-index: 0;
        position: static;
        top: 0;
        background-color: transparent;
      `}
  }
`

const MemoizedHorizonListingCard = React.memo(ListingCard)
const MemoizedRetailItinerary = React.memo(StyledRetailItinerary)
const MemoizedExpressDealCard = React.memo(ExpressDealCard)

const listingsContainerCss = classNames('fly-search-listings-container', 'white-background')
const listingsFullWidth = classNames('fly-search-listings full-width', 'no-padding')

export function Listings(props) {
  const {
    activeTabId,
    departingFrom,
    returningFrom,
    listings,
    exdCarriers = [],
    searchReferralId,
    onListingItemSelect,
    onPackageListingItemSelect,
    tripType,
    brandReference = [],
    searchRequestCabinClass,
    hasExpressDeal,
    vipTier,
    enableExpressDealVip,
    numSlices,
    isReshopExperience,
    reshopPerPersonText,
    isMultipleAirlinesLogoVariant,
    isSinglePassenger,
    passengerCount,
    isPinSelected,
    isPinVariant,
    searchRequest = {},
    onListingsMount,
    isDesktop,
    logExdClick,
    searchKey,
    setPinnedItins,
    handleTabChange,
    selectedDepartureSummaries = [],
    isMultiDestination,
    searchSlices,
    showBaggageFeeLink = true,
    showLoadingOverlay,
    packageType,
    logCarouselArrow,
    pinnedItins,
    listingsMetaData = {},
    hasUSDestinationsOnly,
    alwaysRenderFirstN,
    partner,
    recommendedFlightBannerListing,
    recommendedFlightBannerListingProps,
    isHorizonListing,
    showStaticXsellAtBannerPosition,
    isDesktopStaticBannerPositionVariant,
    inlinePhoneSalesBanner,
  } = props
  const { totalFilteredItineraries } = listingsMetaData

  const { params = {} } = searchRequest

  const rawRecommendedListings = useSelector((state) => state.recommendedListings?.[1])
  const recommendedListings = useMemo(() => rawRecommendedListings ?? [], [rawRecommendedListings])
  // isNotReshopAndWLAndPackages synonymous with isStandalone
  const isNotReshopAndWLAndPackages = useSelector(
    (state) => state?.pclnBootstrapData?.isNotReshopAndWLAndPackages
  )
  const isRecommendedSearch = activeTabId === 1 && recommendedListings.length > 0

  const isPricelineOnly = useFeature('whiteLabel.isPricelineOnly')
  const isPackageListings = useSelector((state) => state?.pclnBootstrapData?.isPackageListings)
  const hasPriorPackageSelection = useSelector(
    (state) => state?.pclnBootstrapData?.hasPriorPackageSelection === true
  )
  const PCLN_BOOTSTRAP_DATA = useSelector((state) => state?.pclnBootstrapData)
  const airSearchRequestModel = useSelector((state) => state?.airSearchRequestModel)

  const isPackageCrossSellEnabled = useFeature('whiteLabel.packagesCrossSellEnabled', true)

  // useRef
  const listingsContainerRef = useRef(null)
  const adCount = useRef(0)
  const retailCount = useRef(1)

  const vipBadgePortalRootRef = useRef(null)
  const [isVIPBadgeIntersecting, setIsVIPBadgeIntersecting] = useState(false)
  const [hasVipBadge, setHasVipBadge] = useState(false)

  useEffect(() => {
    const el = document.getElementById('vip-badge')
    if (!el) return
    vipBadgePortalRootRef.current = el
    setHasVipBadge(true)

    const observer = new IntersectionObserver(([entry]) => {
      setIsVIPBadgeIntersecting(entry.isIntersecting)
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      vipBadgePortalRootRef.current = null
    }
  }, [])

  // hooks
  const { departureSummaryData } = useDepartureSummary({ selectedDepartureSummaries })
  const packagePrice = useSelector(({ listingsMetaData }) =>
    activeTabId === 1 ? listingsMetaData?.defaultPackagePrice : listingsMetaData?.selectedPackagePrice
  )

  useEffect(() => {
    if (
      activeTabId === 2 &&
      tripType === 'RT' &&
      selectedDepartureSummaries?.length > 0 &&
      airSearchRequestModel?.filter?.type === 'SLICE_SELECTION' &&
      airSearchRequestModel?.filter?.sliceFilters?.[0]?.selectedFareBrand?.selectedPriceIndex >= 1
    ) {
      try {
        const allListings = [...recommendedListings, ...listings].filter(
          (l) => l.slices?.length > 0 && !l.isExpressDeal
        )
        const _listingsWithNegFares = allListings.filter((l) =>
          l.fareBrands?.some((fb) => ['Save', '-'].includes(fb.pricing?.text))
        )
        const listingsWithNegFares = _listingsWithNegFares.map((listing) => {
          return {
            fb: listing.fareBrands.map(({ price, pricing }) => ({
              p: getPriceByType({ prices: price, type: 'AVG_PRICE' })?.amount,
              d: `${['Save', '-'].includes(pricing?.text) ? '-' : '+'}${pricing?.amountInFloat}`,
            })),
            a: listing.marketingAirlines?.map(({ code }) => code)?.join?.('-'),
            f: listing.isFused,
          }
        })

        const numFareBrandsWithNegPricing = listingsWithNegFares.reduce(
          (acc, listing) => acc + listing.fb.filter((fb) => fb.d.includes('-')).length,
          0
        )

        const selectedFlight1 = {
          price: getPriceByType({ prices: selectedDepartureSummaries[0]?.price, type: 'AVG_PRICE' })?.amount,
          airline: selectedDepartureSummaries[0]?.marketingAirlines
            ?.map((airline) => airline?.code)
            ?.join('-'),
          fareBrand: selectedDepartureSummaries[0]?.fareBrandName,
        }

        PCLNAnalytics.log({
          category: 'Flight Listings',
          action: 'Fare Brands',
          label: 'Fare Brands with Negative Pricing',
          requestId: listingsMetaData.requestId,
          appCode: PCLN_BOOTSTRAP_DATA?.isMobile ? 'MOBILEWEB' : 'DESKTOP',
          activeTabId,
          selectedFlight1: JSON.stringify(selectedFlight1),
          numListingsWithNegFares: listingsWithNegFares.length.toString(),
          numFareBrandsWithNegPricing: numFareBrandsWithNegPricing.toString(),
          listingsWithNegFares: JSON.stringify(listingsWithNegFares),
        })
      } catch (error) {
        PCLNAnalytics.log({
          category: 'Flight Listings',
          action: 'Fare Brands',
          label: 'Error Logging Fare Brands with Negative Pricing',
          error: error.message,
          requestId: listingsMetaData.requestId,
          appCode: PCLN_BOOTSTRAP_DATA?.isMobile ? 'MOBILEWEB' : 'DESKTOP',
          activeTabId,
        })
      }
    }
  }, [
    activeTabId,
    listingsMetaData.requestId,
    PCLN_BOOTSTRAP_DATA?.isMobile,
    recommendedListings,
    listings,
    tripType,
    selectedDepartureSummaries,
    airSearchRequestModel?.filter,
  ])

  const ListingComponent = isHorizonListing ? MemoizedHorizonListingCard : MemoizedRetailItinerary

  const sbsTestIdPrefix = !isDesktop ? 'retail-mobile-sliceId' : 'retail-new-sliceId'

  const modifyNow = params?.userActionType === 'MODIFY'
  const stickyDepartureSummaryCard = hasUSDestinationsOnly

  // User Login
  const authUser = useSelector((state) => state.pclnBootstrapData?.authUser)
  const isUserLoggedIn = Boolean(authUser)

  const { getVipTierBadgeForItinerary } = usePkgFlyListingVipBadge({
    isPackageListings,
    isPricelineOnly,
    isUserLoggedIn,
    isHorizonListing,
  })

  const isVipDealEligible = isNotReshopAndWLAndPackages && isUserLoggedIn
  const totalPassengerCount = countFlightPassengers({
    passengers: passengerCount,
    pclnBootstrapData: PCLN_BOOTSTRAP_DATA,
  })
  const isSocialProofVariant = pclnExpt.isVariant('AIR_EXD_SOCIAL_PROOFING')

  const isPriceDisclosureCopyVariant = pclnExpt.isVariant('PKG_FLY_STANDALONE_LISTINGS_BAG_COPY') && !partner

  const fireImpressionCarrierSort = () => {
    if (isNotReshopAndWLAndPackages) {
      pclnExpt.fireImpression('AIR_BRAND_ATTR_ORDER_DL')
    }
  }
  const fireImpressionFareAttrSort = () => {
    if (isNotReshopAndWLAndPackages) {
      pclnExpt.fireImpression('AIR_FARE_ATTR_SORT')
    }
  }
  const isCarrierSortVariant = pclnExpt.getTestState('AIR_BRAND_ATTR_ORDER_DL') === 'VARIANT'
  const isFlatAttributeOrderSETI = isNotReshopAndWLAndPackages && pclnExpt?.isVariant('AIR_FARE_ATTR_SORT')
  const isLoyalty = pclnExpt.isVariant('AIR_FARE_BRAND_ATTR_LOYALTY')
  const fireImpressionLoyaltyAttr = () => {
    pclnExpt.fireImpression('AIR_FARE_BRAND_ATTR_LOYALTY')
  }
  const isLegroom = pclnExpt.isVariant('AIR_FARE_BRAND_SPEC_LEGROOM')
  const fireImpressionLegroom = () => {
    pclnExpt.fireImpression('AIR_FARE_BRAND_SPEC_LEGROOM')
  }
  const isCheapestPriceColorEligible = isNotReshopAndWLAndPackages && ['RT', 'OW'].includes(tripType)
  const expressDeal =
    recommendedListings?.find((listing) => listing.isExpressDeal) ||
    listings?.find((listing) => listing.isExpressDeal)
  const isSplitExd = expressDeal?.isFused
  const hasCheckedBag = hasCheckedBagOnAllSlices(expressDeal?.slice)
  useEffect(() => {
    if (hasExpressDeal) {
      if (isSplitExd) {
        pclnExpt.fireImpression('AIR_EXD_REDUCE_CARRIER_COUNT')
      }

      if (!isUserLoggedIn) {
        pclnExpt.fireImpression('AIR_LISTING_DEAL_MSG_SIGNUP')
      }
      pclnExpt.fireImpression('AIR_EXD_LISTING_SHOW_EXACT_TIME')
      if (hasCheckedBag) {
        pclnExpt.fireImpression('AIR_EXD_CHECKED_BAG_INCL')
      }
      pclnExpt.fireImpression('AIR_EXD_SOCIAL_PROOFING')
    }
  }, [hasCheckedBag, hasExpressDeal, isSplitExd, isUserLoggedIn])

  useEffect(() => {
    const latencyHit = listingsMetaData?.latencyHit
    const cacheHitObject = {
      type: 'cache',
      page_name: 'listings',
      product_name: 'air',
      item_name: 'hit',
    }
    if (latencyHit === 0) {
      logEvent('api_call', { ...cacheHitObject, item_name: 'miss' })
    } else if (latencyHit === 1) {
      logEvent('api_call', { ...cacheHitObject })
    } else if (latencyHit === 2) {
      logEvent('api_call', { ...cacheHitObject, item_name: 'partial_hit' })
    }
  }, [listingsMetaData?.latencyHit])

  // Domestic package recommended sort SETI (PCLN + whitelabel).
  useEffect(() => {
    if (isPackageListings && hasUSDestinationsOnly && isRecommendedSearch) {
      pclnExpt.fireImpression('PKG_FLY_LISTINGS_DOMESTIC_ML_SORT')
    }
  }, [isPackageListings, hasUSDestinationsOnly, isRecommendedSearch])

  useEffect(() => {
    if (isPackageListings) {
      pclnExpt.fireImpression('PKG_FLY_RC_LISTINGS_PRICE_PLACEMENT')
    }
  }, [isPackageListings])

  const isPricePlacementVariant =
    pclnExpt.getTestState('PKG_FLY_RC_LISTINGS_PRICE_PLACEMENT') === 'VARIANT' && isPackageListings

  const isIncrementalPricingContext = hasPriorPackageSelection || activeTabId > 1
  const isProposalPricingMissing = listingsMetaData?.isProposalPricingMissing === true
  const isAllProposalPricesMissing = getIsAllProposalPricesMissing({
    hasPriorPackageSelection,
    isProposalPricingMissing,
    activeTabId,
    packageType,
  })
  // should fire for PCLN (standalone and packages path) and not whitelabel
  useEffect(() => {
    if (!partner) {
      pclnExpt.fireImpression('PKG_FLY_STANDALONE_LISTINGS_BAG_COPY')
      pclnExpt.fireImpression('PKG_FLY_LISTINGS_MOVE_PHONE_BANNER')
    }
  }, [partner])

  const cheapestItinIDs = useMemo(() => {
    if (!listings || !listings.length) {
      return new Set()
    }

    let allListings = []
    let minPrice = Infinity

    if (listings?.length > 0) {
      if (recommendedListings?.length > 0) {
        allListings = [...recommendedListings]
      }
      // filters out ads within listings arrays
      allListings = [...allListings, ...listings].filter((listing) => Object.hasOwn(listing, 'id'))
    }

    // gets minPrice on page
    allListings.forEach((listing) => {
      const listingPrice = Math.ceil(listing.totalPriceWithDecimal?.price)
      if (minPrice > listingPrice) {
        minPrice = listingPrice
      }
    })

    const newCheapestItinIDs = allListings
      .filter((listing) => {
        const listingPrice = Math.ceil(listing.totalPriceWithDecimal?.price)
        return listingPrice === minPrice
      })
      .map((obj) => obj.id)

    return new Set(newCheapestItinIDs)
  }, [listings, recommendedListings])

  useEffect(() => {
    if (onListingsMount) {
      const listingsLoadTimerEnd = new Date()
      onListingsMount(listingsLoadTimerEnd)
    }
  }, [onListingsMount])

  useEffect(() => {
    if (listingsContainerRef.current) {
      listingsContainerRef.current.focus()
    }
  }, [listingsContainerRef])

  const onExdSelect = ({ searchKey, itinerary, hasAutoScroll = false }) => {
    const queryString = window.location.search
    let path = window.location.pathname
    // Ensure path ends with '/' for proper routing to /deals
    path = path.endsWith('/') ? path : `${path}/`

    let queries = queryString ? `${queryString}&search-key=${searchKey}` : `?search-key=${searchKey}`

    // Add `isFireFly` flag for FireFly EXD
    if (itinerary.priceKey && itinerary.itemKey) {
      queries = `${queries}&priceKey=${itinerary.priceKey}&itemKey=${itinerary.itemKey}`
    }

    // example: m/fly/search/NYC-SFO-20201213/SFO-NYC-20201216/deals
    const redirectPath = `${path}deals`

    const { workflowId } = window?.PCLN_BOOTSTRAP_DATA

    const setiUrlFragment = hasAutoScroll ? '#express-deal-options-subheader' : ''

    if (queries) {
      window.open(
        `${redirectPath}${queries}&new-tab=true&workflow-id=${workflowId}${setiUrlFragment}`,
        '_blank'
      )
    } else {
      window.open(`${redirectPath}?new-tab=true&workflow-id=${workflowId}${setiUrlFragment}`, '_blank')
    }
  }

  const onExdClick = (e, itinerary) => {
    if (e.key === 'Enter' || e.keyCode === 13 || e.key === ' ' || e.keyCode === 32 || e.type === 'click') {
      e.preventDefault()
      onExdSelect({ searchKey, itinerary })
    }
  }

  const onExdLinkClick = (e, itinerary) => {
    if (e.key === 'Enter' || e.keyCode === 13 || e.key === ' ' || e.keyCode === 32 || e.type === 'click') {
      const hasAutoScroll = true
      e?.preventDefault?.()
      logInternalLink({ itinerary })
      onExdSelect({ searchKey, itinerary, hasAutoScroll })
    }
  }

  const getAdCount = () => {
    adCount.current = adCount + 1
    return adCount
  }

  const getRetailCount = () => {
    retailCount.current = retailCount + 1
    return retailCount
  }

  /** Priceline account creation (VIP / join); path is same-origin so staging and WL hosts resolve correctly. */
  const PCLN_CREATE_ACCOUNT_PATH = '/home/join/'

  // TODO: Is this still needed
  const openLoginModal = (e) => {
    e?.stopPropagation()
    if (window.PCLN) {
      logExdSigninClick()
      window.PCLN.pclnEventDispatcher.dispatch('GLOBAL_PCLN_LAUNCH_SIGNIN_MODAL')
    }
  }

  const openCreateAccountPage = (e) => {
    e?.stopPropagation()
    logExdCreateAccountClick()
    window.location.assign(new URL(PCLN_CREATE_ACCOUNT_PATH, window.location.origin).href)
  }

  /**
   * TODO: Test the logic
   * Handle pin click
   * */
  const handlePinClick = (e, priceKey) => {
    e?.stopPropagation()
    e.preventDefault()

    setPinnedItins(priceKey)
  }

  const getExpressDealNode = ({ itinerary, showSeeAllLink }) => {
    const isCheapestValue = cheapestItinIDs.has(itinerary.id)
    const isPriceColorChanged = !isCheapestValue && isCheapestPriceColorEligible
    // only testing for base EXD Card
    const isExactTimeVariant = pclnExpt.isVariant('AIR_EXD_LISTING_SHOW_EXACT_TIME')
    const isCheckedBagExdVariant = pclnExpt.isVariant('AIR_EXD_CHECKED_BAG_INCL')

    return {
      isFlightCard: true,
      component: (
        <MemoizedExpressDealCard
          mx={[2, 2, 2, 2, 0]}
          {...getExdProps({
            searchRequest: searchRequest,
            onExdClick: (e) => {
              logExdClick(itinerary)
              onExdClick(e, itinerary)
            },
            onExdLinkClick: (e) => {
              logExdClick(itinerary)
              onExdLinkClick(e, itinerary)
            },
            openLoginModal: openLoginModal,
            itinerary,
            userLoggedIn: isUserLoggedIn,
            exdCarriers,
            vipTier,
            pclnExpt,
            enableExpressDealVip,
            isCheckedBagExdVariant,
          })}
          isSinglePassenger={isSinglePassenger}
          pinnedItins={pinnedItins}
          itinerary={itinerary}
          showPin={isPinVariant}
          onPinClick={(e) => {
            handlePinClick(e, itinerary.priceKey)
          }}
          isShowTotalPriceExDMultiAdult={isNumOfAdultPassengers(params) && !isPackageListings}
          passengerCount={totalPassengerCount}
          isMobile={!isDesktop}
          showSeeAllLink={showSeeAllLink}
          isPriceColorChanged={isPriceColorChanged}
          isExactTimeVariant={isExactTimeVariant}
          socialProof={
            isSocialProofVariant && (
              <Highlight textStyle='body3' className='text-nowrap'>
                Frequently booked
              </Highlight>
            )
          }
        />
      ),
    }
  }

  // TODO: Move to helpers - Start
  const getSliceOnClick = ({ itinerary, handleDepartureSliceClick, activeTabId, props }) => {
    const { numSlices, tripType, searchRequest, shouldForceFlyDetails } = props
    const { params } = searchRequest
    let sliceOnClick = null
    if (activeTabId < numSlices) {
      sliceOnClick = getNextSliceClick(itinerary, handleDepartureSliceClick, isPackageListings)
    } else if (activeTabId === numSlices || tripType === 'OW') {
      if (params['usp-cache-key'] && params['cart-key'] && !shouldForceFlyDetails) {
        sliceOnClick = goToCheckout({ cb: onPackageListingItemSelect, itinerary, isPackageListings })
      } else {
        sliceOnClick = goToDetailsClick(onListingItemSelect, itinerary.id, itinerary, getRetailCount())
      }
    }

    return sliceOnClick
  }

  const getMerchandisingList = ({ itinerary }) => {
    if (!isReshopExperience) {
      const badgeMerchandising = itinerary?.slices[0].merchandisingBadges || []

      // returning array of rendering code, must fix later
      return badgeMerchandising.map((badge) => {
        return (
          <Flex key={badge.title} mr={1}>
            <Badge colorScheme={badge?.colorScheme} textTransform='capitalize' size='captionSmall'>
              {badge?.title}
            </Badge>
          </Flex>
        )
      })
    }
  }

  const getNoChangeFeeBadge = ({ itinerary, tripType }) => {
    return (
      itinerary?.itinerary?.fareBrands?.[0]?.ancillaries?.some(
        (farebrand) => farebrand.tag === 'REBOOKING' && farebrand.offerType === 'C'
      ) && tripType !== 'MD'
    )
  }
  // Move to a helpers - End

  const getListingComponent = ({ itinerary, retailIndex, props }) => {
    const {
      activeTabId,
      handleDepartureSliceClick,
      onListingItemSelect,
      cancelTextColor,
      cancelFooterIcon,
      hasEuropeFlight,
      isShelves,
      passengerCount,
      pinnedItins,
      packageType,
    } = props

    if (activeTabId > 1 && isShelves) {
      const originPriceKey = itinerary.priceKey
      if (itinerary?.fareBrands?.length === 0) {
        logNoBrandedFareSecondSliceEvent({
          tripType,
          airlineName: itinerary?.airlines?.[0]?.name,
          isFused: itinerary.isFused,
        })
        return null
      }
      if (originPriceKey !== itinerary.fareBrands[0].priceKey) {
        itinerary.priceKey = itinerary.fareBrands[0].priceKey
      }
    }

    const SBSData = getSBSItineraryData({
      itinerary,
      isDepartureSummary: false,
      isUserLoggedIn,
      activeTabId,
      departureSummaryData: departureSummaryData?.length
        ? departureSummaryData[departureSummaryData.length - 1]
        : null,
      tripType,
      brandReference,
      pclnExpt,
      isSinglePassenger,
      packageType,
      isShelves,
      passengerCount,
      hasPriorPackageSelection,
    })

    const sliceOnClick = getSliceOnClick({
      itinerary,
      handleDepartureSliceClick,
      activeTabId,
      packagePrice,
      props,
    })

    const merchandisingList = getMerchandisingList({ itinerary, props })
    const isNoChangeFee = getNoChangeFeeBadge({ itinerary, tripType })
    const vipTierBadge = getVipTierBadgeForItinerary(itinerary)
    const isCheapestValue = cheapestItinIDs.has(itinerary.id)

    let ItineraryComponent = (
      <ListingComponent
        {...SBSData}
        isHeroImage={retailIndex === 0 && !hasExpressDeal}
        onClick={sliceOnClick}
        handleDepartureSliceClick={handleDepartureSliceClick}
        signInOnClick={openLoginModal}
        isMobile={!isDesktop}
        label={merchandisingList}
        itinerary={itinerary}
        onListingItemSelect={onListingItemSelect}
        testId={`${sbsTestIdPrefix}-${activeTabId}-index-${retailIndex++}`}
        isCancellable={!!Number(itinerary.voidWindowInfo?.hoursLeft)}
        cancelTextColor={cancelTextColor}
        modifyNow={modifyNow}
        cancelFooterIcon={cancelFooterIcon}
        hasEuropeFlight={hasEuropeFlight}
        voidWindowInfo={itinerary?.voidWindowInfo}
        searchRequestCabinClass={searchRequestCabinClass}
        isNoChangeFee={isNoChangeFee}
        isReshopExperience={isReshopExperience}
        reshopPerPersonText={reshopPerPersonText}
        pclnExpt={pclnExpt}
        logViewAllOptionsClick={logViewAllOptionsClick}
        isMultipleAirlinesLogoVariant={isMultipleAirlinesLogoVariant}
        passengerCount={totalPassengerCount}
        handlePinClick={(e) => {
          handlePinClick(e, itinerary.priceKey)
        }}
        pinnedItins={pinnedItins}
        showPin={isPinVariant}
        packageType={packageType}
        hasUSDestinationsOnly={hasUSDestinationsOnly}
        logIconTray={logIconTray}
        isPackageListings={isPackageListings}
        isWL={!!partner}
        isForcedFareSelection={isNotReshopAndWLAndPackages}
        isVipDealEligible={isVipDealEligible}
        isAllInclusive={isNotReshopAndWLAndPackages}
        isHighlightBasicRestrictions={isNotReshopAndWLAndPackages}
        fireImpressionFareAttrSort={fireImpressionFareAttrSort}
        isNewPerPersonPrice={isPackageListings}
        showFareBrandInternational={pclnExpt.isVariant('AIR_LISTINGS_BRANDED_FARES_INTERNATIONAL')}
        showPriceSkeleton={false}
        fireImpressionCarrierSort={fireImpressionCarrierSort}
        isCarrierSortVariant={isCarrierSortVariant}
        isFlatAttributeOrderSETI={isFlatAttributeOrderSETI}
        isLoyalty={isLoyalty}
        fireImpressionLoyaltyAttr={fireImpressionLoyaltyAttr}
        isLegroom={isLegroom}
        fireImpressionLegroom={fireImpressionLegroom}
        vipTierBadge={vipTierBadge}
        showDifferentPriceColor={isCheapestPriceColorEligible}
        isCheapest={isCheapestValue}
        showIncrementalPrice={isIncrementalPricingContext && !isProposalPricingMissing}
        isAllProposalPricesMissing={isAllProposalPricesMissing}
      />
    )

    return {
      hide: false,
      priceKey: itinerary.priceKey,
      id: itinerary?.id,
      isFlightCard: true,
      component: ItineraryComponent,
    }
  }

  let retailIndex = 0
  let dynamicBannerPlacementIndex = 0
  const listItems = (listings, listType) => {
    const {
      xSellAd,
      adsContainer,
      sponsoredAdsContainer,
      pclnGptDartAds,
      disableAds,
      listingIsLoading = false,
    } = props

    const relocateStaticXsellListType = isRecommendedSearch ? 'recommendedListings' : 'listings'
    // When relocating, drop the first default static banner (rendered at ~position
    // 10 of the same section) so it isn't shown twice.
    let didDropFirstStaticXsell = false

    let listingsNodes = listings.map((itinerary, index) => {
      itinerary = structuredClone(itinerary)
      // Packages Xsell/Cross sell Ad
      if (itinerary.crossSellAd && xSellAd !== undefined) {
        if (
          showStaticXsellAtBannerPosition &&
          listType === relocateStaticXsellListType &&
          !didDropFirstStaticXsell
        ) {
          didDropFirstStaticXsell = true
          return null
        }
        return {
          hide: false,
          component: xSellAd,
        }
      }

      /**
       * Inline Ads between listings
       * TODO: fetch Ad Ids from buildOrdered Listings
       * */

      if (itinerary.ad && !disableAds) {
        const adId = get(pclnGptDartAds, `[${itinerary.adIndex}].gptDivId`)

        if (adId && adsContainer && !isDesktop) {
          return {
            hide: false,
            inlineAds: true,
            adId,
            component: (
              <div>
                {React.cloneElement(adsContainer, {
                  adId,
                  key: adId,
                  gaIndex: getAdCount(),
                  departingFrom,
                  returningFrom,
                  searchReferralId,
                  inlineAds: true,
                })}
              </div>
            ),
          }
        }
      }

      /**
       * Inline Ads between listings
       * TODO: fetch Ad Ids from buildOrdered Listings
       * */
      if (
        !listingIsLoading &&
        !disableAds &&
        sponsoredAdsContainer &&
        itinerary.isSponsoredAd &&
        itinerary.adId
      ) {
        return {
          hide: false,
          isSponsoredAd: true,
          component: (
            <div>
              {React.cloneElement(sponsoredAdsContainer, {
                adId: itinerary.adId,
                key: itinerary.adId,
                gaIndex: getAdCount(),
                departingFrom,
                returningFrom,
                searchReferralId,
                isSponsored: true,
              })}
            </div>
          ),
        }
      }

      if (itinerary.isExpressDeal) {
        const showSeeAllLink = index === 0

        return getExpressDealNode({ itinerary, showSeeAllLink })
      }

      if (!itinerary.ad && !itinerary.isExpressDeal && !itinerary.crossSellAd) {
        return getListingComponent({ itinerary, retailIndex: retailIndex++, props })
      }

      return null
    })

    listingsNodes = listingsNodes.filter((elem) => !!elem)

    let exdNode = null

    let pinnedNodes = listingsNodes.filter((elem) => {
      if (pinnedItins.has(elem?.priceKey)) {
        return elem
      } else if (pinnedItins.has(elem?.component?.props?.itinerary?.priceKey)) {
        if (exdNode) {
          return
        } else {
          exdNode = elem
          return elem
        }
      }
    })

    if (pinnedNodes.length > 0) {
      listingsNodes = isPinSelected ? pinnedNodes : listingsNodes
    }

    if (isPackageListings && isPricelineOnly) {
      const signInBanner = {
        hide: false,
        id: 'signInBannerPKG',
        component: <PackagesVIPBanner isSignedIn={isUserLoggedIn} />,
      }
      // add VIP banner to the second spot in the entire listing
      listingsNodes.splice(2, 0, signInBanner)
    }

    const bundleDealSortTest = pclnExpt.isVariant('PKG_AIR_DYNAMIC_BANNER_SORT_TEST')
    let shouldInsertRecommendedPackageDeal = !!recommendedFlightBannerListing && isPackageCrossSellEnabled

    if (
      (bundleDealSortTest || isDesktopStaticBannerPositionVariant) &&
      isRecommendedSearch &&
      listType === 'listings'
    ) {
      // means it has recommended listings and the banner was inserted there
      shouldInsertRecommendedPackageDeal = false
    }

    if (isDesktopStaticBannerPositionVariant && listType === relocateStaticXsellListType) {
      dynamicBannerPlacementIndex = insertDesktopStaticBannerPositionNodes({
        listingsNodes,
        listType,
        relocateStaticXsellListType,
        recommendedListings,
        dynamicBannerPlacementIndex,
        xSellAd,
        shouldInsertRecommendedPackageDeal,
        recommendedFlightBannerListing,
        recommendedFlightBannerListingProps,
      })
    } else if (shouldInsertRecommendedPackageDeal) {
      const bannerArgs = {
        listingsNodes,
        listType,
        bundleDealSortTest,
        recommendedListings,
        dynamicBannerPlacementIndex,
      }
      const [bannerIndex, placementIndex, updatedIndex] = calculateBannerIndex(bannerArgs)
      dynamicBannerPlacementIndex = updatedIndex

      const isPrecededBySponsoredAd = listingsNodes[placementIndex - 1]?.isSponsoredAd ?? false

      const recommendedPackageDealNode = {
        hide: false,
        afterSponsoredAd: isPrecededBySponsoredAd,
        id: `pkg-banner-${recommendedFlightBannerListing.priceKey}`,
        component: (
          <RecommendedFlightBannerWrapper
            recommendedFlightBannerListing={recommendedFlightBannerListing}
            recommendedFlightBannerListingProps={recommendedFlightBannerListingProps}
            bannerIndex={bannerIndex}
          />
        ),
      }

      if (placementIndex >= listingsNodes.length) {
        listingsNodes.push(recommendedPackageDealNode)
      } else {
        listingsNodes.splice(placementIndex, 0, recommendedPackageDealNode)
      }
    } else if (
      showStaticXsellAtBannerPosition &&
      listType === relocateStaticXsellListType &&
      xSellAd !== undefined
    ) {
      // Relocate the static Xsell banner to the dynamic banner slot (position 4).
      // The top static banner is hidden upstream in ContentRenderer.
      // Pass bundleDealSortTest: false so the banner lands at position 4 rather than
      // being appended to the end of the recommended listing.
      const [, placementIndex] = calculateBannerIndex({
        listingsNodes,
        listType,
        bundleDealSortTest: false,
        recommendedListings,
        dynamicBannerPlacementIndex,
      })

      const staticXsellNode = {
        hide: false,
        id: 'pkg-top-static-xsell-relocated',
        component: xSellAd,
      }

      if (placementIndex >= listingsNodes.length) {
        listingsNodes.push(staticXsellNode)
      } else {
        listingsNodes.splice(placementIndex, 0, staticXsellNode)
      }
    }

    if (inlinePhoneSalesBanner) {
      const insertIndex = getPhoneSalesBannerInsertIndex({
        listingsNodes,
        listType,
        isRecommendedSearch,
        otherFlightsItineraryCount: countFlightListingItems(props.listings),
      })
      if (insertIndex != null) {
        listingsNodes.splice(insertIndex, 0, {
          id: 'phone-sales-banner',
          component: <div data-testid='inline-phone-sales-banner'>{inlinePhoneSalesBanner}</div>,
        })
      }
    }

    listingsNodes = WrapComponentsRenderInView(listingsNodes, alwaysRenderFirstN)

    return listingsNodes
  }

  const destinationAirport = searchSlices?.[0]?.destinationAirport
  const cityName = searchRequest?.airports?.[destinationAirport]?.cityName

  const packageListingHeaderProps = getPackageListingHeaderProps({
    listings,
    recommendedListings,
    activeTabId,
    packageType,
    searchRequest,
    searchSlices,
    hasPriorPackageSelection,
    isProposalPricingMissing,
  })

  // PKG_FLY_RC_LISTINGS_PRICE_PLACEMENT drops this below the price disclosure on return
  // slices, so it is built once and rendered in one of two spots.
  const showDepartureSummary = activeTabId > 1 && departureSummaryData.length > 0
  let departureSummaryElement = null
  if (showDepartureSummary && isHorizonListing) {
    departureSummaryElement = (
      <DepartureSummary
        handleTabChange={() => handleTabChange({ id: 1 })}
        airline={departureSummaryData[0].airline}
        arrival={departureSummaryData[0].arrival}
        fareBrandName={departureSummaryData[0].fareBrandName}
        departing={departureSummaryData[0].departing}
        price={departureSummaryData[0].price}
        stopsText={departureSummaryData[0].stopsText}
        durationText={departureSummaryData[0].durationText}
        marketingAirlines={departureSummaryData[0].marketingAirlines}
        activeTabId={activeTabId}
      />
    )
  } else if (showDepartureSummary) {
    departureSummaryElement = (
      <DepartureSummaryWrapper
        bg='background.lightest'
        mb={2}
        mx={departureSummaryData?.length > 1 ? [1, 1, 1, 4, 0] : [2, 2, 2, 2, 0]}
        isSticky={stickyDepartureSummaryCard}
        stickyTop={isPackageListings ? '62px' : '74px'}
      >
        <DepartureSummaryCarousel
          currentSlide={showLoadingOverlay ? 0 : departureSummaryData.length - 1}
          isMultiDestination={isMultiDestination}
          searchSlices={searchSlices}
          selectedSlices={departureSummaryData.map((summary, index) => ({
            ...summary,
            isSticky: stickyDepartureSummaryCard,
            changeFlightHandler: returnToDepartureTab(handleTabChange, index),
          }))}
          onSlideChange={logCarouselArrow}
        />
      </DepartureSummaryWrapper>
    )
  }

  const isSignupToastVariant =
    hasExpressDeal && !isUserLoggedIn && pclnExpt.isVariant('AIR_LISTING_DEAL_MSG_SIGNUP')

  useExpressDealSignInToast({
    enabled: isSignupToastVariant,
    onSignInClick: openLoginModal,
    onCreateAccountClick: openCreateAccountPage,
  })

  return (
    <>
      {!isUserLoggedIn &&
        (isVIPBadgeIntersecting && vipBadgePortalRootRef.current
          ? createPortal(
              <AnchoredToastViewport side='bottom' align='end' className='z-[100]' />,
              vipBadgePortalRootRef.current
            )
          : !hasVipBadge && <ToastViewport position='topRight' className='z-[100] !top-24' />)}
      <section className={listingsContainerCss}>
        <SrOnly ref={listingsContainerRef} tabIndex={0}>
          {buildNumOfItinsReturnedText(totalFilteredItineraries, listings, activeTabId < numSlices)}
        </SrOnly>

        {!isPricePlacementVariant && departureSummaryElement}

        {activeTabId === 2 && tripType === 'RT' && isNotReshopAndWLAndPackages && departureSummaryData ? (
          <RecommendedFilters departureSummaryData={departureSummaryData} />
        ) : null}

        {isRecommendedSearch && (
          <div>
            {isPackageListings && !isPricePlacementVariant ? (
              <>
                <div className='flex flex-col gap-2 lg:hidden'>
                  <div className='mx-2'>
                    <PackageListingHeaderPrice {...packageListingHeaderProps} currentProduct='FLY' />
                  </div>
                  <PriceDisclosure
                    showBaggageFeeLink={showBaggageFeeLink}
                    isPriceDisclosureCopyVariant={isPriceDisclosureCopyVariant}
                  />
                  <RecommendedListingsHeader cityName={cityName} />
                </div>
                <div className='hidden lg:flex lg:flex-col lg:gap-2'>
                  <div className='flex lg:flex-row lg:justify-between lg:gap-0'>
                    <div className='lg:flex-1'>
                      <RecommendedListingsHeader cityName={cityName} />
                    </div>
                    <div className='mx-2 mb-2 lg:mx-0 lg:flex lg:flex-col lg:items-end lg:gap-1'>
                      <PackageListingHeaderPrice {...packageListingHeaderProps} currentProduct='FLY' />
                    </div>
                  </div>
                  <PriceDisclosure
                    showBaggageFeeLink={showBaggageFeeLink}
                    isPriceDisclosureCopyVariant={isPriceDisclosureCopyVariant}
                  />
                </div>
              </>
            ) : (
              <>
                <PriceDisclosure
                  showBaggageFeeLink={showBaggageFeeLink}
                  isPriceDisclosureCopyVariant={isPriceDisclosureCopyVariant}
                />
                <RecommendedListingsHeader cityName={cityName} />
              </>
            )}
            <ItineraryList className={listingsFullWidth} data-testid='recommended-listings-container'>
              {listItems(recommendedListings, 'recommendedListings')}
            </ItineraryList>
          </div>
        )}

        <div>
          {isRecommendedSearch ? (
            <Heading
              fontWeight='bold'
              fontSize={['16px', '16px', '16px', '16px', '20px']}
              mt={'24px'}
              mb={3}
              px={[2, 2, 2, 2, 0]}
            >
              {activeTabId === 1 ? `Other Flights to ${cityName}` : `Other Return Flights`}
            </Heading>
          ) : (
            <>
              {isPackageListings ? (
                !isPricePlacementVariant && <PackageListingHeaderRow {...packageListingHeaderProps} />
              ) : (
                <ListingsHeader
                  tripType={tripType}
                  activeTabId={activeTabId}
                  searchSlices={searchSlices}
                  packageType={packageType}
                  packagePrice={Math.ceil(packagePrice)}
                  searchRequest={searchRequest}
                />
              )}
              <div className='mb-2'>
                <PriceDisclosure
                  showBaggageFeeLink={showBaggageFeeLink}
                  isPriceDisclosureCopyVariant={isPriceDisclosureCopyVariant}
                />
              </div>
            </>
          )}
        </div>

        {isPricePlacementVariant && departureSummaryElement}

        <ItineraryList className={listingsFullWidth} data-testid='primary-listings-container'>
          {listItems(listings, 'listings')}
        </ItineraryList>
      </section>
    </>
  )
}
